import React, { useState } from 'react';
import { FileDown } from 'lucide-react';
import { salesOrderService } from '../../services/salesOrderService';
import type { CardRefund } from '../../types/cardRefund';
import { brandLabel, formatCentsBRL, formatIsoDateTimeBR } from './cardRefund';

const statusClass: Record<number, string> = {
  1: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  2: 'bg-red-50 text-red-700 border-red-200',
  0: 'bg-amber-50 text-amber-700 border-amber-200',
};

/**
 * Estornos de cartão do pedido, com o comprovante em PDF. Visível para quem lê
 * o pedido; sem estorno, o bloco não aparece. A lista é carregada pela tela do
 * pedido (o mesmo dado decide o botão do cabeçalho).
 */
export const CardRefundsCard: React.FC<{ orderId: number; refunds: CardRefund[] }> = ({ orderId, refunds }) => {
  const [downloading, setDownloading] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (refunds.length === 0) return null;

  const download = async (refundId: number) => {
    setDownloading(refundId);
    setError(null);
    try {
      await salesOrderService.downloadCardRefundPdf(orderId, refundId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao gerar o comprovante');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div>
      <dt className="text-xs text-slate-500 uppercase tracking-wider">Estornos de cartão</dt>
      <dd className="mt-0.5 space-y-1.5">
        {refunds.map((r) => (
          <div key={r.credit_card_refund_id} className="flex items-center gap-2 flex-wrap text-sm text-slate-900">
            <span className="font-medium">{formatCentsBRL(r.amount_cents)}</span>
            <span className="text-slate-500">
              {r.is_total ? 'total' : 'parcial'} · {brandLabel(r.brand)} final {r.last4} · {formatIsoDateTimeBR(r.completed_on || r.created_on)}
            </span>
            <span className={`px-1.5 py-0.5 rounded text-xs font-medium border ${statusClass[r.status] ?? statusClass[0]}`}>{r.status_label}</span>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline disabled:opacity-50"
              onClick={() => download(r.credit_card_refund_id)}
              disabled={downloading === r.credit_card_refund_id}
              title={r.status === 1 ? 'Comprovante de cancelamento (PDF)' : 'Relatório da tentativa (PDF) — não é comprovante'}
            >
              <FileDown size={14} /> {r.status === 1 ? 'Comprovante' : 'PDF'}
            </button>
          </div>
        ))}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </dd>
    </div>
  );
};
